// External packages
import * as React from 'react';
import * as ReactAria from 'react-aria-components';

// Components
import { Button, PlainButton } from 'components/Button';
import { Label } from 'components/Forms';
import { Icon } from 'components/Icons';

// Modules
import { DeleteConfirmationModal } from 'modules/DeleteConfirmationModal';

// Hooks
import {
  MAX_MODEL_SIZE_BYTES,
  useDeleteVisionModel,
  useGetVisionModels,
  useUploadVisionModel,
} from 'api/visionModel';
import { useToast } from 'hooks/useToast';

const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

/**
 * Upload/replace/delete the YOLO recognition model (.tflite) of a place.
 *
 * Model files are 30+ MB, so the component validates the file locally
 * (extension + size) BEFORE any bytes leave the browser, and shows a real
 * upload progress bar while the file streams to the server. Replacing a
 * model bumps its version — mobile apps pick the change up from the
 * manifest on their next sync.
 */
export const VisionModelManager: React.FC<{ placeId: string }> = ({
  placeId,
}) => {
  const toast = useToast();
  const { data: models, isLoading } = useGetVisionModels();
  const { mutate: uploadModel, isPending: isUploading } =
    useUploadVisionModel();
  const { mutate: deleteModel, isPending: isDeleting } =
    useDeleteVisionModel();

  const [progress, setProgress] = React.useState<number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const model = models.find((m) => m.placeId === placeId);

  const handleSelect = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    // Validate locally before sending 30+ MB over the wire.
    if (!file.name.toLowerCase().endsWith('.tflite')) {
      toast.error('Dozvoljene su samo .tflite datoteke.');
      return;
    }
    if (file.size > MAX_MODEL_SIZE_BYTES) {
      toast.error(
        `Datoteka je prevelika (${formatBytes(file.size)}). Maksimalna veličina je ${formatBytes(MAX_MODEL_SIZE_BYTES)}.`
      );
      return;
    }

    setProgress(0);
    uploadModel(
      { placeId, file, onProgress: setProgress },
      { onSettled: () => setProgress(null) }
    );
  };

  if (isLoading) {
    return <Icon name="loader" className="animate-spin size-6" />;
  }

  return (
    <div>
      <Label className="font-bold">AI model za prepoznavanje (YOLO)</Label>
      <p className="text-xs text-gray-500 mb-4">
        .tflite model koji mobilna aplikacija koristi za prepoznavanje
        eksponata u ovoj sobi. Nakon uploada model je odmah dostupan
        aplikacijama kroz manifest.
      </p>

      {model ? (
        <div className="border border-gray-200 rounded-lg p-4 flex flex-wrap items-center gap-4">
          <Icon name="box" className="size-8 text-gray-500" />
          <div className="flex-1 min-w-40">
            <p className="font-medium break-all">
              {model.originalFileName || model.fileName}
            </p>
            <p className="text-xs text-gray-500">
              {formatBytes(model.sizeBytes)} · verzija {model.version} ·
              ažurirano {new Date(model.updatedAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-2">
            <ReactAria.FileTrigger
              acceptedFileTypes={['.tflite']}
              onSelect={handleSelect}
            >
              <Button
                variant="outline"
                isPending={isUploading}
                isDisabled={isUploading || isDeleting}
              >
                Zamijeni model
              </Button>
            </ReactAria.FileTrigger>
            <DeleteConfirmationModal
              isOpen={deleteModalOpen}
              onOpenChange={setDeleteModalOpen}
              isPending={isDeleting}
              onDelete={() =>
                deleteModel(model.id, {
                  onSuccess: () => setDeleteModalOpen(false),
                })
              }
              modalTrigger={
                <Button
                  variant="ghost"
                  colorScheme="tertiary"
                  icon={<Icon name="trash" />}
                  isDisabled={isUploading || isDeleting}
                >
                  Obriši
                </Button>
              }
            />
          </div>
        </div>
      ) : (
        <ReactAria.FileTrigger
          acceptedFileTypes={['.tflite']}
          onSelect={handleSelect}
        >
          <PlainButton
            isDisabled={isUploading}
            className="h-14 w-full cursor-pointer text-center rounded-lg border border-dashed border-gray-300 px-4 text-gray-500 transition-colors hover:border-gray-400"
          >
            {isUploading
              ? 'Upload u tijeku…'
              : 'Dodaj .tflite model (max 100 MB)'}
          </PlainButton>
        </ReactAria.FileTrigger>
      )}

      {progress !== null && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Upload modela…</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-black transition-all"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
